import { ReactComponent as TextFormatting } from 'resources/images/lyrics/text-formatting.svg';
import { ReactComponent as BoldIcon } from 'resources/images/lyrics/editor-bold.svg';
import { ReactComponent as HeadingIcon } from 'resources/images/lyrics/editor-heading.svg';
import { ReactComponent as ItalicIcon } from 'resources/images/lyrics/editor-italic.svg';
import { ReactComponent as StrikeIcon } from 'resources/images/lyrics/editor-strike.svg';
import { ReactComponent as ListIcon } from 'resources/images/lyrics/editor-list.svg';
import { MenuBarIcon } from 'app/components/Song/Lyrics/MenuBarIcon';
import React from 'react';
import { Editor } from '@tiptap/react';
import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { Lyrics2Configuration } from 'types/lyrics.types';

interface TextFormattingMenuProps {
  editor: Editor;
  disabled: boolean;
  config: Lyrics2Configuration;
  isOpen: boolean;
  onToggle: (open: boolean) => void;
}

export const TextFormattingMenu = ({
  editor,
  config,
  disabled,
  isOpen,
  onToggle,
}: TextFormattingMenuProps) => {
  const extensions = Object.keys(config.extensions);
  const buttonSx = { mr: 1, width: '32px', height: '32px' };

  return (
    <Popover onClose={() => onToggle(false)} onOpen={() => onToggle(true)}>
      <PopoverTrigger>
        <MenuBarIcon
          active={isOpen}
          disabled={disabled}
          icon={TextFormatting}
          data-testid="formatting"
        />
      </PopoverTrigger>
      <PopoverContent width="auto" borderRadius={4} borderColor="borderBase">
        <PopoverBody display="inline-flex" px={3} py={2} bg="surface1">
          {extensions.includes('Bold') && (
            <MenuBarIcon
              disabled={disabled}
              icon={BoldIcon}
              onClick={() =>
                (editor as any)?.chain().focus().toggleBold().run()
              }
              active={editor.isActive('bold')}
              data-testid="button-apply-style-bold"
              sx={buttonSx}
            />
          )}
          {extensions.includes('Italic') && (
            <MenuBarIcon
              disabled={disabled}
              icon={ItalicIcon}
              onClick={() =>
                (editor as any)?.chain().focus().toggleItalic().run()
              }
              active={editor.isActive('italic')}
              data-testid="button-apply-style-italic"
              sx={buttonSx}
            />
          )}
          {extensions.includes('Strike') && (
            <MenuBarIcon
              disabled={disabled}
              icon={StrikeIcon}
              onClick={() =>
                (editor as any)?.chain().focus().toggleStrike().run()
              }
              active={editor.isActive('strike')}
              data-testid="button-apply-style-strike"
              sx={buttonSx}
            />
          )}
          {extensions.includes('BulletList') && (
            <MenuBarIcon
              disabled={disabled}
              icon={ListIcon}
              onClick={() =>
                (editor as any)?.chain().focus().toggleBulletList().run()
              }
              active={editor.isActive('bulletList')}
              data-testid="button-apply-style-list"
              sx={buttonSx}
            />
          )}
          {extensions.includes('Heading') && (
            <MenuBarIcon
              disabled={disabled}
              icon={HeadingIcon}
              onClick={() =>
                (editor as any)
                  ?.chain()
                  .focus()
                  .toggleHeading({ level: 1 })
                  .run()
              }
              active={editor.isActive('heading', { level: 1 })}
              data-testid="button-apply-style-heading"
              sx={buttonSx}
            />
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
